import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {
    GetExternalMeetings as GetExternalMeetingsQuery,
    ExternalMeetingFragment,
    GetExternalMeetingsVariables as GetExternalMeetingsQueryVariables,
} from 'nutshell-graphql-types';

import {nutApolloClient} from '../../../../../apollo-client';

import type {ExternalServiceTypeFrontend} from '../../types';

import GET_EXTERNAL_MEETINGS from '../queries/get-external-meetings.graphql';

export const LIMIT = 50;

export const useGetExternalMeetings = (input: {
    isLogged: boolean;
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend;
    skip?: boolean;
    shouldPoll?: boolean;
}) => {
    const variables = {
        isLogged: input.isLogged,
        serviceTypes: getServiceTypes(input.serviceType),
    } as const;
    const {data, error, fetchMore, refetch, loading} = useQuery<
        GetExternalMeetingsQuery,
        GetExternalMeetingsQueryVariables
    >(GET_EXTERNAL_MEETINGS, {
        variables: {
            ...variables,
            first: LIMIT,
        },
        fetchPolicy: 'cache-and-network',
        skip: input.skip,
        pollInterval: input.shouldPoll ? 5000 : 0,
    });

    const externalMeetingsData = data ? data.externalMeetings : undefined;

    const externalMeetings: ExternalMeetingFragment[] = externalMeetingsData
        ? externalMeetingsData.edges.map((edge) => edge.node)
        : [];

    const isLoading = !data && loading; // only show loading state on first load
    const pageInfo = externalMeetingsData ? externalMeetingsData.pageInfo : null;
    const count = externalMeetingsData ? externalMeetingsData.count : 0;
    const endCursor =
        externalMeetingsData && externalMeetingsData.pageInfo
            ? externalMeetingsData.pageInfo.endCursor
            : null;
    const startCursor =
        externalMeetingsData && externalMeetingsData.pageInfo
            ? externalMeetingsData.pageInfo.startCursor
            : null;

    const handleFetchPreviousPage = () => {
        if (pageInfo && pageInfo.hasPreviousPage) {
            const nextRequestVariables = {
                ...variables,
                before: startCursor,
                last: LIMIT,
                first: null,
            } as const;

            return fetchMore({
                variables: nextRequestVariables,
                // @ts-expect-error
                updateQuery: (
                    prev: GetExternalMeetingsQuery,
                    newResults: {
                        fetchMoreResult: any;
                    }
                ) => {
                    // fetchMore will not cache this data under nextRequestVariables, so we need to do this manually
                    // so future calls with the same variables can rely on the cache.
                    nutApolloClient.writeQuery({
                        query: GET_EXTERNAL_MEETINGS,
                        variables: nextRequestVariables,
                        data: newResults.fetchMoreResult,
                    });

                    return newResults.fetchMoreResult;
                },
            });
        }

        return Promise.reject();
    };

    const handleFetchNextPage = () => {
        if (pageInfo && pageInfo.hasNextPage) {
            const nextRequestVariables = {
                ...variables,
                after: endCursor,
                first: LIMIT,
            } as const;

            return fetchMore({
                variables: nextRequestVariables,
                // @ts-expect-error
                updateQuery: (
                    prev: GetExternalMeetingsQuery,
                    newResults: {
                        fetchMoreResult: GetExternalMeetingsQuery;
                    }
                ) => {
                    nutApolloClient.writeQuery({
                        query: GET_EXTERNAL_MEETINGS,
                        variables: nextRequestVariables,
                        data: newResults.fetchMoreResult,
                    });

                    return newResults.fetchMoreResult;
                },
            });
        }

        return Promise.reject();
    };

    return {
        externalMeetings,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        refetch,
        count,
        fetchPreviousPage: handleFetchPreviousPage,
        fetchNextPage: handleFetchNextPage,
    };
};

function getServiceTypes(
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend
): ExternalServiceType[] {
    switch (serviceType) {
        case 'ZOOM':
            return ['ZOOM'];
        case 'GOOGLE_MEET':
            return ['GOOGLE_MEET'];
        case 'CALLTRACKERFX':
            return ['CALLTRACKERFX'];
        case 'MICROSOFT_TEAMS':
            return ['MICROSOFT_TEAMS'];
        case 'PHONES':
            return ['AIRCALL', 'CALLRAIL', 'RINGCENTRAL', 'DIALPAD', 'KIXIE'];
        default:
            return [];
    }
}
